import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AtSign, Building2, Calendar, Hash, LogOut, Mail, Phone, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { InfoTile } from "../components/ui/Card";
import { StatusBadge } from "../components/ui/Badge";
import { formatDate, initials } from "../utils/format";

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    if (!user) return null;

    const handleLogout = () => {
        logout();
        navigate("/", { replace: true });
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
            <div className="bg-slate-900/40 backdrop-blur-2xl border border-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden">
                <div className="h-32 bg-gradient-to-r from-[#8b5cf6] to-[#6d28d9] relative">
                    <div className="absolute -bottom-12 left-8 p-1 bg-slate-900 rounded-2xl border-4 border-slate-900">
                        <div className="w-24 h-24 bg-slate-800 rounded-xl flex items-center justify-center text-3xl font-bold text-white">
                            {initials(user.name)}
                        </div>
                    </div>
                </div>

                <div className="pt-16 pb-8 px-6 md:px-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white tracking-tight">{user.name}</h1>
                            <div className="flex items-center gap-2 text-slate-400 mt-1">
                                <AtSign size={14} />
                                <span className="text-sm font-medium">{user.username}</span>
                                <StatusBadge value={user.role} />
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl font-bold text-sm transition-all active:scale-95"
                        >
                            <LogOut size={18} />
                            Logout
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
                        <InfoTile icon={<Mail size={20} />} label="Email Address" value={user.email || "N/A"} />
                        <InfoTile icon={<Phone size={20} />} label="Phone Number" value={user.phone || "N/A"} />
                        <InfoTile icon={<ShieldCheck size={20} />} label="Role" value={user.role} />
                        <InfoTile icon={<Hash size={20} />} label="User ID" value={`#${user.id.toString().padStart(4, "0")}`} />
                        {user.createdAt && <InfoTile icon={<Calendar size={20} />} label="Member Since" value={formatDate(user.createdAt)} />}
                        {user.customer && (
                            <InfoTile
                                icon={<Building2 size={20} />}
                                label="Linked Customer"
                                value={`${user.customer.name} (#${user.customer.customerId})`}
                            />
                        )}
                    </div>

                    <div className="mt-10 pt-8 border-t border-slate-800 flex items-center gap-3">
                        <img src="/logo.png" alt="Logo" className="w-8 h-8 object-contain" />
                        <span className="text-slate-500 text-xs font-medium">Reztra CRM</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}